// Запись технического журнала (002: data-model.md, observability.log_entries; FR-029 – FR-032).
// В записи нет ФИО, почты, адресов, паролей и токенов (FR-030): сообщение — из шаблона кода,
// записи — номерами.
import { z } from '../kernel/zod.ts';
import { IdSchema, OperationIdSchema, TimestampSchema } from '../kernel/common.ts';

/** Уровни в таблице: ниже info — только стандартный вывод. */
export const logLevels = ['info', 'warn', 'error', 'fatal'] as const;
export type LogLevel = (typeof logLevels)[number];
export const LogLevelSchema = z.enum(logLevels);

/** Уровень словом — бейдж на экране «Технический журнал». */
export const logLevelLabels = {
  info: 'Информация',
  warn: 'Предупреждение',
  error: 'Ошибка',
  fatal: 'Отказ',
} as const satisfies Record<LogLevel, string>;

/** Модули сервиса и HTTP-ядро (docs/architecture.md, раздел 4). */
export const logModules = [
  'kernel',
  'access',
  'audit',
  'observability',
  'http',
  'delivery',
] as const;
export type LogModule = (typeof logModules)[number];
export const LogModuleSchema = z.enum(logModules);

/** Процессы одного образа: сервер приложения, обработчик задач, обработчик оповещений, команда. */
export const logProcesses = ['api', 'worker', 'alerter', 'cli'] as const;
export type LogProcess = (typeof logProcesses)[number];
export const LogProcessSchema = z.enum(logProcesses);

/** Сообщение записи — не длиннее 2 000 знаков. */
export const LOG_MESSAGE_LIMIT = 2000;

/** Номер записи, к которой относится строка: тип и uuid (FR-029). */
export const LogRecordRefSchema = z.object({ type: z.string(), id: IdSchema });
export type LogRecordRef = z.infer<typeof LogRecordRefSchema>;

export const LogEntrySchema = z.object({
  /** Номер строки журнала (bigint) — строкой. */
  id: z.string(),
  at: TimestampSchema,
  level: LogLevelSchema,
  module: LogModuleSchema,
  process: LogProcessSchema,
  /** Версия и коммит сборки. */
  version: z.string(),
  operation: OperationIdSchema.optional(),
  /** Код ошибки из каталога. */
  code: z.string().optional(),
  message: z.string().max(LOG_MESSAGE_LIMIT),
  records: z.array(LogRecordRefSchema),
  durationMs: z.number().int().nonnegative().optional(),
  /** Для ошибок: значения из запросов замаскированы (serializeError 001). */
  stack: z.string().optional(),
});
export type LogEntry = z.infer<typeof LogEntrySchema>;
